import { BindAll } from 'lodash-decorators'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiHomework } from '@xt/client/api'
import { action, computed, observable } from 'mobx'
import { mapStickerPaths } from '@xt/client/utils/sticker'
import type { ReplayReviewResult, ReplayCommentResult } from './types.d'
import { UserMemberTypeEnum } from '@xt/client/enums'
import { EnumLoadResult } from '@xt/client/utils/infinite-loading'

export interface BaseCommentInfo<T extends number | string> {
  id: T
  memberId: string
  memberNickname: string
  memberType: number
  toMemberId?: string | null
  toMemberNickname?: string | null
  toMemberType?: number | null
  toCommentId: T | null
  content: string
  stickersContent: string
  commentPictureList?: null | Array<unknown>
  rootCommentId?: string
  createTime: number
  // 前端格式化好的表情数据
  stickers: string[]
  // 评阅没有此项数据，但为了视图处理方便 都兼容一下此属性
  like: boolean
  likeCount: number
}

export interface CommentItem<T extends number | string> extends BaseCommentInfo<T> {
  children: Array<BaseCommentInfo<T>>
  // 是否有更多的子评论 前端追加的
  hasMoreChildren: boolean
}

// 使用lastId处理分页
type Pagination<T extends string | number> = {
  pageSize: number
  lastId?: T
}

@BindAll()
export class UserComments {
  constructor() {}

  // 分页数据
  private reviewPagination: Pagination<number> = {
    pageSize: 10
  }

  private commentPagination: Pagination<string> = {
    pageSize: 10
  }

  // 当前正在请求
  @observable isCommentLoading: boolean = false

  @observable isReviewLoading: boolean = false

  // 评阅列表
  @observable reviewList: Array<CommentItem<number>> = []

  // 评阅总数
  @observable reviewTotal: number = 0

  // 是否还有更多的评阅
  @observable reviewHasMore: boolean = true

  // 评论列表
  @observable commentList: Array<CommentItem<string>> = []

  // 评论总数
  @observable commentTotal: number = 0

  // 是否还有更多的评论
  @observable commentHasMore: boolean = true

  // 作业是否被助教评阅过
  @computed get isHomeworkReviewed() {
    return this.reviewList.length > 0 && this.reviewList.some(item => item.memberType === UserMemberTypeEnum.TeachingAssistant)
  }

  // mock数据接口
  public loadMockComment(result: { commentTotal: number; comments: Array<CommentItem<string>> }) {
    if (result.comments.length) {
      this.commentPagination.lastId = result.comments[result.comments.length - 1].id
    }
    this.updateCommentMetaData(result.comments.length === this.commentPagination.pageSize, result.commentTotal)
    this.appendCommentData(result.comments)
  }

  // 加载用户评论数据
  public async loadUserComment(momentId: string) {
    if (!momentId) return EnumLoadResult.Cancel

    if (!this.commentHasMore) return EnumLoadResult.Complete

    if (this.isCommentLoading) return EnumLoadResult.Cancel

    this.updateCommentLoading(true)

    const [error, result] = await $service<{ commentTotal: number; comments: Array<CommentItem<string>> }>({
      url: EnumApiHomework.MomentCommentListV2,
      method: 'GET',
      data: { momentId },
      params: {
        momentId,
        rootCommentId: 0,
        ...this.commentPagination
      }
    })

    this.updateCommentLoading(false)

    if (error) return EnumLoadResult.Error

    if (result.comments.length) {
      this.commentPagination.lastId = result.comments[result.comments.length - 1].id
    }
    this.updateCommentMetaData(result.comments.length === this.commentPagination.pageSize, result.commentTotal)
    this.appendCommentData(result.comments)

    return result.comments.length === this.commentPagination.pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
  }

  // 加载子评论数据
  public async loadChildComment(momentId: string, rootCommentId: string) {
    const item = this.commentList.find(i => i.id === rootCommentId)

    if (!item || !item.children.length) return

    const [error, result] = await $service<{ commentTotal: number; comments: Array<BaseCommentInfo<string>> }>({
      url: EnumApiHomework.MomentCommentListV2,
      data: {
        momentId
      },
      params: {
        momentId,
        rootCommentId,
        pageSize: 5,
        lastId: item.children[item.children.length - 1].id
      }
    })

    if (error) return

    this.appendChildCommentData(rootCommentId, result.comments)
  }

  // 加载用户评阅数据
  public async loadUserReview(homeworkId: number) {
    if (!homeworkId) return EnumLoadResult.Cancel

    if (this.isReviewLoading) return EnumLoadResult.Cancel

    if (!this.reviewHasMore) return EnumLoadResult.Complete

    this.updateReviewLoading(true)

    const [error, result] = await $service<{ commentTotal: number; comments: Array<BaseCommentInfo<number>> }>({
      url: EnumApiHomework.HomeworkCommentV2,
      method: 'GET',
      data: { id: homeworkId },
      params: {
        id: homeworkId,
        rootCommentId: 0,
        pageSize: this.reviewPagination.pageSize,
        lastId: this.reviewPagination.lastId
      }
    })

    this.updateReviewLoading(false)

    // 等待下一次触发加载行为
    if (error) return EnumLoadResult.Error

    if (result.comments.length) {
      this.reviewPagination.lastId = result.comments[result.comments.length - 1].id
    }
    this.updateReviewMetaData(result.comments.length === this.reviewPagination.pageSize, result.commentTotal)
    this.appendReviewData(result.comments)

    return result.comments.length === this.reviewPagination.pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
  }

  // 回复评论
  public async replyComment(params: {
    content: string
    stickers: string[]
    momentId: string
    // 以下参数属于回复二级评论
    rootCommentId?: string
    toCommentId?: string
    toMemberId?: string
  }) {
    const [error, result] = await $service<ReplayCommentResult>({
      method: 'POST',
      url: EnumApiHomework.MomentComment,
      data: {
        content: params.content,
        contentLength: params.content.length,
        momentId: params.momentId,
        rootCommentId: params.rootCommentId ?? '0',
        stickersContent: params.stickers.join(','),
        toCommentId: params.toCommentId ?? null,
        toMemberId: params.toMemberId ?? null
      }
    })

    if (error) {
      return false
    }

    // 先判断下是否所有子评论加载出来了，如果加载完毕才修改前端数据
    const item = this.commentList.find(i => i.id === params.rootCommentId)
    if (item && item.hasMoreChildren === false) {
      this.insertReplyCommentData(result)
    } else if (!params.rootCommentId) {
      this.insertReplyCommentData(result)
    }

    this.updateCommentMetaData(this.commentHasMore, this.commentTotal + 1)

    return true
  }

  // 回复助教评阅
  public async replyReview(params: { content: string; stickers: string[]; homeworkId: number }) {
    const [error, result] = await $service<ReplayReviewResult>({
      method: 'POST',
      url: EnumApiHomework.HomeworkReply,
      data: {
        homeworkId: params.homeworkId,
        replyContent: params.content,
        replyContentNum: params.content.length,
        stickersContent: params.stickers.join(','),
        toCommentId: ''
      }
    })

    if (error) {
      return false
    }

    // 如果分页数据还没有加载完 不更新前端缓存
    if (!this.reviewHasMore) {
      this.appendReviewData({ ...result, stickers: [], like: false, likeCount: 0 })
    }
    this.updateReviewMetaData(this.reviewHasMore, this.reviewTotal + 1)

    return true
  }

  // 删除一个评论或评阅
  public async removeItem(data: { type: 'comment'; commentId: string; rootCommentId: string } | { type: 'review'; commentId: number }) {
    if (data.type === 'comment') {
      const [_error, success] = await $service<boolean>({
        method: 'DELETE',
        url: EnumApiHomework.MomentDeleteComment,
        data: { id: data.commentId }
      })

      if (success === true) {
        this.updateCommentMetaData(this.commentHasMore, this.getAfterRemoveCommentTotal(data.commentId, data.rootCommentId))
        this.removeCommentItem(data.commentId, data.rootCommentId)
        return true
      }
    } else if (data.type === 'review') {
      const [_error, success] = await $service({
        method: 'DELETE',
        url: EnumApiHomework.HomeworkCommentDelete,
        data: { id: data.commentId }
      })

      if (success === true) {
        this.updateReviewMetaData(this.reviewHasMore, Math.max(0, this.reviewTotal - 1))
        this.removeReviewItem(data.commentId)
        return true
      }
    }

    return false
  }

  // 评论点赞
  public async likeComment(commentId: string, rootCommentId: string) {
    const [error, success] = await $service({
      url: EnumApiHomework.MomentLikeComment,
      method: 'PUT',
      data: { commentId, likeType: 'true' }
    })

    if (success === true) {
      this.likeCommentItem(commentId, rootCommentId)
    }

    return success === true
  }

  private getAfterRemoveCommentTotal(commentId: string, rootCommentId: string): number {
    if (commentId === rootCommentId) {
      const parent = this.commentList.find(comment => comment.id === rootCommentId)

      if (parent?.children) {
        return Math.max(this.commentTotal - parent.children.length - 1, 0)
      }
    }

    return Math.max(this.commentTotal - 1, 0)
  }

  @action
  private removeCommentItem(commentId: string, rootCommentId: string) {
    if (commentId === rootCommentId) {
      this.commentList = this.commentList.filter(i => i.id !== commentId)
    } else {
      this.commentList = this.commentList.map(i => {
        if (i.id === rootCommentId) {
          i.children = i.children.filter(j => j.id !== commentId)
        }

        return i
      })
    }
  }

  @action
  private likeCommentItem(commentId: string, rootCommentId: string) {
    this.commentList = this.commentList.map(i => {
      if (i.id === rootCommentId) {
        if (rootCommentId === commentId) {
          i.like = true
          i.likeCount += 1
        } else {
          i.children = i.children.map(j => {
            if (j.id === commentId) {
              j.like = true
              j.likeCount += 1
            }

            return j
          })
        }
      }

      return i
    })
  }

  @action
  private removeReviewItem(commentId: number) {
    this.reviewList = this.reviewList.filter(i => i.id !== commentId)
  }

  @action
  private updateCommentLoading(isLoading: boolean) {
    this.isCommentLoading = isLoading
  }

  @action
  private updateReviewLoading(isLoading: boolean) {
    this.isReviewLoading = isLoading
  }

  // 更新评阅元数据
  @action
  private updateReviewMetaData(hasMore: boolean, total: number) {
    this.reviewHasMore = hasMore
    this.reviewTotal = total
  }

  // 新增评阅列表数据
  @action
  private appendReviewData(review: BaseCommentInfo<number> | Array<BaseCommentInfo<number>>) {
    if (!Array.isArray(review)) {
      review = [review]
    }

    this.reviewList = [
      ...this.reviewList,
      ...review.map(item => {
        return {
          ...item,
          children: [],
          hasMoreChildren: false,
          like: false,
          likeCount: 0,
          stickers: mapStickerPaths(item.stickersContent || '')
        } satisfies CommentItem<number>
      })
    ]
  }

  // 更新评论元数据
  @action
  private updateCommentMetaData(hasMore: boolean, total: number) {
    this.commentHasMore = hasMore
    this.commentTotal = total
  }

  // 新增评论列表数据 仅分页时使用
  @action
  private appendCommentData(comment: CommentItem<string> | Array<CommentItem<string>>) {
    if (!Array.isArray(comment)) {
      comment = [comment]
    }

    this.commentList = [
      ...this.commentList,
      ...comment.map(item => {
        return {
          ...item,
          children: Array.isArray(item.children)
            ? item.children.map(c => ({
                ...c,
                stickers: mapStickerPaths(c.stickersContent || ''),
                like: c.like === true,
                likeCount: typeof c.likeCount === 'number' ? c.likeCount : 0
              }))
            : [],
          stickers: mapStickerPaths(item.stickersContent || ''),
          like: item.like === true,
          likeCount: typeof item.likeCount === 'number' ? item.likeCount : 0,
          hasMoreChildren: Array.isArray(item.children) ? item.children.length >= 5 : false
        } satisfies CommentItem<string>
      })
    ]
  }

  // 追加子评论
  @action
  private appendChildCommentData(rootCommentId: string, comments: Array<BaseCommentInfo<string>>) {
    this.commentList = this.commentList.map(item => {
      if (item.id === rootCommentId) {
        item.children = [
          ...item.children,
          ...comments.map(comment => {
            return {
              ...comment,
              stickers: mapStickerPaths(comment.stickersContent || ''),
              like: comment.like === true,
              likeCount: typeof comment.likeCount === 'number' ? comment.likeCount : 0
            } satisfies BaseCommentInfo<string>
          })
        ]

        item.hasMoreChildren = comments.length === 5
      }

      return item
    })
  }

  // 回复评论 后端返回结果 将其按照规则插入到前端缓存中
  @action
  private insertReplyCommentData(comment: ReplayCommentResult) {
    if (comment.rootCommentId === '0') {
      this.commentList = [
        {
          ...comment,
          children: [],
          hasMoreChildren: false,
          stickers: mapStickerPaths(comment.stickersContent || ''),
          like: false,
          likeCount: 0
        } satisfies CommentItem<string>,
        ...this.commentList
      ]
    } else {
      this.commentList = this.commentList.map(parent => {
        if (parent.id === comment.rootCommentId) {
          parent.children.push({
            ...comment,
            stickers: mapStickerPaths(comment.stickersContent),
            like: false,
            likeCount: 0
          })
        }

        return parent
      })
    }
  }

  @action
  public reset() {
    this.isCommentLoading = false
    this.commentTotal = 0
    this.commentList = []
    this.commentHasMore = true
    this.commentPagination = {
      pageSize: 10
    }

    this.isReviewLoading = false
    this.reviewTotal = 0
    this.reviewList = []
    this.reviewHasMore = true
    this.reviewPagination = {
      pageSize: 10
    }
  }
}
